import Alert from "uicomponents/Alert";
import styles from "./CurrentEmployees.module.css";
import { CURRENT_EMPLOYEES_FORM_TITLE } from "pages/CensusAndCurrentInformation/utils/constants";
import { useEffect, useState } from "react";
import { useCensusAndCurrentInformationContext } from "pages/CensusAndCurrentInformation/CensusAndCurrentInformation";
import { CurrentCoverageFormValues } from "pages/CensusAndCurrentInformation/utils/initialFormValues";
import { currentEmployeesValidationSchema } from "pages/CensusAndCurrentInformation/utils/validationSchema";
import {
  trackFormError,
  trackPageViewUserStep,
  trackSaveAndContinueUserStep,
} from "utils/analytics/trackEvent";
import { ConvertToISODate, errorMessageMap } from "utils/helper";
import EmployeeTable from "uicomponents/EmployeeTable";
import { CurrentEmployeesType } from "types";
import { IEmployeeCensusFormRow } from "uicomponents/EmployeeTable/EmployeeTable";
import Tooltip from "uicomponents/Tooltip";
import {
  useDeleteDependentCensusMutation,
  useUpsertDependentAndEmployeeCensusMutation,
  useUpdateStepMutation,
  EmployeeCensusSourceEnum,
} from "types/generated/operations";
import { getProspectUuid } from "utils/idHelpers";
import {
  GenderTypeEnum,
  ParticipantTypeEnum,
  PlanTierEnum,
} from "types/generated/schema";
import { mapBEStepstoMatchFE } from "pages/CensusAndCurrentInformation/utils/helper";
import newstyles from "../../CensusAndCurrentInformation.module.css";
import {
  STEPS_WITH_COBRA,
  STEPS_WITHOUT_COBRA,
} from "pages/CensusAndCurrentInformation/utils/constants";
import Stepper from "uicomponents/Stepper";
import { getStepNumberFromURL } from "utils/helper";
import Error from "uicomponents/Error";
interface ICurrentEmployeesProps {
  data: CurrentEmployeesType;
}

const CurrentEmployees = (props: ICurrentEmployeesProps): JSX.Element => {
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageViewUserStep(5, "current empoloyee information");
  }, []);

  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { data } = props;

  const hasCobra = data?.healthInsuranceInformation?.cobraUsage;

  const { previousStep, nextStep, stepperNextStep } =
    useCensusAndCurrentInformationContext();

  const STEPS = hasCobra ? STEPS_WITH_COBRA : STEPS_WITHOUT_COBRA;

  const initialFormValues = CurrentCoverageFormValues(data);

  const [
    upsertDependentAndEmployeeCensus,
    { loading: upsertDependentAndEmployeeCensusLoading },
  ] = useUpsertDependentAndEmployeeCensusMutation();
  const [deleteDependentCensus, { loading: deleteDependentCensusLoading }] =
    useDeleteDependentCensusMutation();
  const [updateStep, { loading: updateStepLoading }] = useUpdateStepMutation();

  const applicationStep = data?.applications?.[0]?.step;
  const currentStep = getStepNumberFromURL();
  const isInValidStep = applicationStep && currentStep > applicationStep;

  if (isInValidStep) {
    return <Error />;
  }

  const handleSubmit = async (values: IEmployeeCensusFormRow) => {
    const filteredValues = values.employees?.filter(
      (emp) => !!emp.firstName && !!emp.zip
    );
    try {
      const currentStep = getStepNumberFromURL();
      if (!filteredValues) {
        return;
      }
      await upsertDependentAndEmployeeCensus({
        variables: {
          prospectId: getProspectUuid(),
          dependentCensuses: filteredValues?.map((eachEmp) => {
            return {
              uuid: eachEmp?.uuid || undefined,
              firstName: eachEmp.firstName || "",
              lastName: eachEmp.lastName || "",
              gender: eachEmp.gender
                ?.toUpperCase()
                .replace(/-/g, "_") as GenderTypeEnum,
              participantType: eachEmp.participantType
                ?.toUpperCase()
                .replace(/-/g, "_") as ParticipantTypeEnum,
              dateOfBirth: ConvertToISODate(eachEmp.dateOfBirth || ""),
              zip: eachEmp.zip || "",
              healthInsInfo: {
                currentPlanName: eachEmp.currentPlanName || "",
                currentEnrollmentTier: eachEmp.currentEnrollTier
                  ? (eachEmp.currentEnrollTier
                      ?.toUpperCase()
                      .replace(/-/g, "_") as PlanTierEnum)
                  : undefined,
                currentMonthlyPremium:
                  Number(eachEmp.currentMonthlyPremium) || undefined,
                monthlyPremiumPostRenewal:
                  Number(eachEmp.monthlyPremiumPostRenewal) || undefined,
              },
            };
          }),
          source: EmployeeCensusSourceEnum.WwHiApp,
        },
      });
      if (data?.applications?.[0]?.step === currentStep) {
        await updateStep({
          variables: {
            uuid: data?.applications?.[0].uuid,
            step: data?.applications?.[0]?.step + 1,
          },
        });
        trackSaveAndContinueUserStep(currentStep, "Current Employees");
      }
      nextStep();
    } catch (error: any) {
      const errorMessage: string = error?.message;
      errorMessageMap[errorMessage] &&
        setErrorMessage(errorMessageMap[errorMessage]);
      trackFormError(error, errorMessage);
      setIsError(true);
      return;
    }
  };

  const handleDelete = async (uuid: string, participantType: string) => {
    try {
      if (!uuid || !participantType) {
        return true;
      }
      await deleteDependentCensus({
        variables: {
          uuid,
          participantType: participantType
            ?.toUpperCase()
            .replace(/-/g, "_") as ParticipantTypeEnum,
        },
      });
      return true;
    } catch (error: any) {
      const errorMessage: string = error?.message;
      setErrorMessage(errorMessageMap[errorMessage] || "Please try again");
      trackFormError(error, errorMessage);
      setIsError(true);
      return false;
    }
  };

  return (
    <div className={newstyles.censusAndCurrentInformationformContainer}>
      <Stepper
        steps={STEPS}
        currentStep={mapBEStepstoMatchFE(getStepNumberFromURL())}
        status={mapBEStepstoMatchFE(data?.applications?.[0].step || 0) || 8}
        onClick={(stepNumber) => stepperNextStep(stepNumber)}
      />
      <div className={newstyles.censusAndCurrentInformationformDiv}>
        <div className={styles.title}>{CURRENT_EMPLOYEES_FORM_TITLE}</div>
        <div className={styles.description}>
          Please provide details on eligible employees (even if some don’t plan
          on applying for coverage) and their current coverage to get you the
          most accurate health insurance pricing.
          <div className={styles.descriptionWithBold}>
            To qualify for a Justworks medical plan, ensure at least two
            employees from separate households enroll in health insurance and
            meet worker's compensation requirements.
          </div>
        </div>
        <div className={styles.description}>
          <Tooltip tooltipText="To get you quotes, the health insurance providers we work with require that Justworks collects this information. It helps them understand who will be getting access to insurance.">
            Why are we collecting this information?
          </Tooltip>
        </div>
        <div className={styles.coloredDiv}>
          <div className={styles.title}>You will need details for:</div>
          <div className={styles.description}>
            <ul>
              <li>
                All benefits-eligible employees, both full-time and part-time
                (working 20+ hours/week) and their dependents.
              </li>
              <li>
                Copies of your current{" "}
                <Tooltip tooltipText="This shows what you pay in health insurance premiums each month to your provider. This amount is generally split between the employer and the employee.">
                  Health Insurance Invoice
                </Tooltip>{" "}
                and{" "}
                <Tooltip tooltipText="Get ready for some acronyms. Plan types might include HMO, PPO, EPO, POS, or HDHP. You can find your plan type(s) on your health insurance invoice.">
                  Plan Type
                </Tooltip>{" "}
                (if applicable)
              </li>
            </ul>
          </div>
        </div>

        <EmployeeTable
          formikProps={{
            initialValues: initialFormValues,
            onSubmit: handleSubmit,
            validationSchema: currentEmployeesValidationSchema,
          }}
          handleBackClick={previousStep}
          handleDelete={handleDelete}
          offerHealthInsurance={
            !!props.data?.healthInsuranceInformation
              ?.currentlyOfferHealthInsurance
          }
          isLoading={
            upsertDependentAndEmployeeCensusLoading ||
            deleteDependentCensusLoading ||
            updateStepLoading
          }
        />
        {isError && <Alert message={errorMessage} />}
      </div>
    </div>
  );
};

export default CurrentEmployees;
